/* global JQuery */
import { Badge2, BadgeType2, ButtonMenu2, Card2, ContentCol2, ContentColSize2, ContentRow2, DialogConfirm2, DialogInfo2, IconFa, Input2, InputType2, LangTextV2, ModalDialogType2, SortingColumn2, SortOrder2, TableWrapper2, Td2, Th2, Tooltip2, Tr2 } from 'bambooo';
import moment from 'moment';
import { fromLonLat } from 'ol/proj';
import { BehaviouralStates as BehaviouralStatesAPI } from '../Api/BehaviouralStates';
import { EncounterCategories as EncounterCategoriesAPI } from '../Api/EncounterCategories';
import { Organization as OrganizationAPI } from '../Api/Organization';
import { SightingMovement as SightingMovementAPI } from '../Api/SightingMovement';
import { SightingFishingVessel as SightingFishingVesselAPI } from '../Api/SightingFishingVessel';
import { Sightings as SightingsAPI } from '../Api/Sightings';
import { Species as SpeciesAPI } from '../Api/Species';
import { User as UserAPI } from '../Api/User';
import { Vehicle as VehicleAPI } from '../Api/Vehicle';
import { VehicleDriver as VehicleDriverAPI } from '../Api/VehicleDriver';
import { Lang } from '../Lang';
import { Nav } from '../Nav';
import { UtilDistanceCoast } from '../Utils/UtilDistanceCoast';
import { UtilLocation } from '../Utils/UtilLocation';
import { UtilSelect } from '../Utils/UtilSelect';
import { SightingPopup } from '../Utils/SightingPopup';
import { UtilSpeciesSilhouette } from '../Utils/UtilSpeciesSilhouette';
import { LocationDisplay } from '../Widget/LocationDisplay';
import { ReactionDisplay } from '../Widget/ReactionDisplay';
import { SightingAnalytics } from '../Widget/SightingAnalytics';
import { SightingDashboard } from '../Widget/SightingDashboard';
import { SightingExport } from '../Widget/SightingExport';
import { SightingMap, SightingMapObjectType } from '../Widget/SightingMap';
import { SightingYearCompare } from '../Widget/SightingYearCompare';
import { SpeciesDisplay } from '../Widget/SpeciesDisplay';
import { BasePage } from './BasePage';
import { MovementSettingsModal } from './Sighting/MovementSettingsModal';
import { SpeciesProfile as SpeciesProfilePage } from './Species/Profile';
import { SightingDeletedModal } from './Sighting/SightingDeletedModal';
import { SightingEditModal } from './Sighting/SightingEditModal';
import { SightingFilter } from './Sighting/SightingFilter';
import { ToursMap } from './Tours/TourMap';
const PAGE_SIZE = 30;
const escapeHtml = (s) => s
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
/**
 * Lookup maps the sighting map popover needs to resolve ids → display names.
 */
/**
 * Wrap any (case-insensitive) match of `term` in `<mark>` so search hits are
 * highlighted in the rendered cells. HTML in the source text is escaped first;
 * the term is regex-escaped before matching.
 */
const highlight = (text, term) => {
    if (text === '') {
        return '';
    }
    const escaped = escapeHtml(text);
    if (term === '') {
        return escaped;
    }
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
    return escaped.replace(new RegExp(`(${escapedTerm})`, 'giu'), '<mark>$1</mark>');
};
/**
 * Sighting page.
 */
export class Sighting extends BasePage {
    constructor() {
        super();
        this._name = 'sighting';
        this._map = null;
        this._sightingDialog = new SightingEditModal();
        this._sightingDeletedDialog = new SightingDeletedModal();
        this._movementSettingsDialog = new MovementSettingsModal();
        this._sightingDialog.setOnSave(async () => {
            const sid = this._sightingDialog.getSightingId();
            /*
             * Add-flow is not wired yet — the modal doesn't capture tour_id/unid/etc.
             * needed for an INSERT, so the backend save endpoint is update-only.
             */
            if (sid === null) {
                this._toast.fire({ icon: 'error', title: Lang.i().l('Creating new sightings via this dialog is not supported yet.') });
                return;
            }
            try {
                const ok = await SightingsAPI.save({
                    id: sid,
                    vehicle_id: this._sightingDialog.getVehicle(),
                    vehicle_driver_id: this._sightingDialog.getVehicleDriver(),
                    beaufort_wind: this._sightingDialog.getBeaufortWind(),
                    date: this._sightingDialog.getDateSight(),
                    tour_start: this._sightingDialog.getTourStart(),
                    tour_end: this._sightingDialog.getTourEnd(),
                    duration_from: this._sightingDialog.getDurationFrom(),
                    duration_until: this._sightingDialog.getDurationUntil(),
                    location_begin: this._sightingDialog.getPositionBegin(),
                    location_end: this._sightingDialog.getPositionEnd(),
                    species_id: this._sightingDialog.getSpecie(),
                    species_count: this._sightingDialog.getSpeciesCount(),
                    reaction_id: this._sightingDialog.getReaction(),
                    other: this._sightingDialog.getOther(),
                    other_vehicle: this._sightingDialog.getOtherBoats(),
                    note: this._sightingDialog.getNote()
                });
                if (ok) {
                    if (this._onLoadTable) {
                        this._onLoadTable();
                    }
                    this._toast.fire({ icon: 'success', title: Lang.i().l('Sighting saved.') });
                    this._sightingDialog.hide();
                }
                else {
                    this._toast.fire({ icon: 'error', title: Lang.i().l('Sighting save failed.') });
                }
            }
            catch (message) {
                this._toast.fire({ icon: 'error', title: message });
            }
        });
        this._sightingDeletedDialog.setOnSave(async () => {
            const tid = this._sightingDeletedDialog.getSightingId();
            if (tid === null) {
                this._toast.fire({ icon: 'error', title: Lang.i().l('ID is not set!') });
                this._sightingDeletedDialog.hide();
                return;
            }
            try {
                if (await SightingsAPI.delete({
                    id: tid,
                    description: this._sightingDeletedDialog.getDescription()
                })) {
                    if (this._onLoadTable) {
                        this._onLoadTable();
                    }
                    this._toast.fire({ icon: 'success', title: Lang.i().l('Sighting delete success.') });
                }
            }
            catch (message) {
                this._toast.fire({ icon: 'error', title: message });
            }
            this._sightingDeletedDialog.hide();
        });
    }
    /**
     * loadContent
     */
    async loadContent() {
        var _a;
        this.addNavbarButton('Add sighting', 'fas fa-plus', () => {
            this._sightingDialog.setTitle(new LangTextV2({ text: 'Add new sighting' }));
            this._sightingDialog.resetValues();
            this._sightingDialog.show();
        });
        // shared mutable state across loaders / renderers ----------------------------------------
        const sort = {
            id: SortOrder2.NONE,
            tour_id: SortOrder2.NONE,
            date: SortOrder2.DESC,
            tour_start: SortOrder2.DESC,
            create_datetime: SortOrder2.NONE,
            update_datetime: SortOrder2.NONE
        };
        const filterValues = {
            period_from: '',
            period_to: '',
            species_id: 0,
            organization_id: 0,
            vehicle_id: 0,
            vehicle_driver_id: 0,
            search: '',
            include_old: false
        };
        let inHeaderSearch = '';
        let totalCount = 0;
        let loadedCount = 0;
        /*
         * Mirrors what's been streamed onto the map; fed into the crossfilter dashboard
         * and re-used to redraw the map when the dashboard filters change.
         */
        const loadedEntries = [];
        /*
         * The search highlight should match anything currently filtering the result —
         * either the in-header quick-search or the modal's "search" field.
         */
        const activeHighlight = () => inHeaderSearch === '' ? filterValues.search : inHeaderSearch;
        // Build the filter payload sent to the backend; drop the empty fields.
        const buildApiFilter = (offset, limit) => {
            const apiFilter = {
                order: {
                    id: sort.id,
                    tour_id: sort.tour_id,
                    date: sort.date,
                    tour_start: sort.tour_start,
                    create_datetime: sort.create_datetime,
                    update_datetime: sort.update_datetime
                },
                limit,
                offset
            };
            if (filterValues.period_from !== '') {
                apiFilter.period_from = filterValues.period_from;
            }
            if (filterValues.period_to !== '') {
                apiFilter.period_to = filterValues.period_to;
            }
            if (filterValues.species_id > 0) {
                apiFilter.species_id = filterValues.species_id;
            }
            if (filterValues.organization_id > 0) {
                apiFilter.organization_id = filterValues.organization_id;
            }
            if (filterValues.vehicle_id > 0) {
                apiFilter.vehicle_id = filterValues.vehicle_id;
            }
            if (filterValues.vehicle_driver_id > 0) {
                apiFilter.vehicle_driver_id = filterValues.vehicle_driver_id;
            }
            /*
             * The in-header quick-search wins over the modal's search when both are set;
             * it's the more "active" UI signal.
             */
            const search = inHeaderSearch === '' ? filterValues.search : inHeaderSearch;
            if (search !== '') {
                apiFilter.search = search;
            }
            if (filterValues.include_old) {
                apiFilter.include_old = true;
            }
            return apiFilter;
        };
        // Filter card ---------------------------------------------------------------------------
        const content = this._wrapper.getContentWrapper().getContent();
        const rowFilter = new ContentRow2(content.getContentFluidElement());
        const filter = new SightingFilter();
        filter.appendTo(new ContentCol2(rowFilter, ContentColSize2.col12).getElement());
        // Lookup maps + initial loads -----------------------------------------------------------
        const mspecies = new Map();
        const mvehicles = new Map();
        const mdrivers = new Map();
        const mbehaviours = new Map();
        const mencates = new Map();
        const morganizations = new Map();
        /*
         * Bundle passed to _addSightingToMap so the map popover can resolve
         * vehicle/driver/organization names (filled by the loads below; the map
         * markers are only built afterwards, so the maps are populated by then).
         */
        const sightingLookups = {
            species: mspecies,
            vehicles: mvehicles,
            drivers: mdrivers,
            organizations: morganizations,
            behaviours: mbehaviours,
            encounterCategories: mencates
        };
        const [species, vehicles, drivers, behaviours, encates, organizations] = await Promise.all([
            SpeciesAPI.getList(),
            VehicleAPI.getList(),
            VehicleDriverAPI.getList(),
            BehaviouralStatesAPI.getList(),
            EncounterCategoriesAPI.getList(),
            OrganizationAPI.getOrganizationByUser()
        ]);
        if (species) {
            for (const s of species) {
                mspecies.set(s.id, s);
            }
            this._sightingDialog.setSpeciesList(species);
            filter.setSpeciesList(species);
        }
        if (vehicles) {
            for (const v of vehicles) {
                mvehicles.set(v.id, v);
            }
            /*
             * Operational pickers (new sighting modal + top filter) only get
             * active boats. mvehicles above still holds every vehicle so old
             * rows referencing a retired boat keep rendering its name.
             */
            const activeVehicles = vehicles.filter((v) => v.in_use && !v.isdeleted);
            this._sightingDialog.setVehicleList(activeVehicles);
            filter.setVehicleList(activeVehicles);
        }
        if (drivers) {
            for (const d of drivers) {
                mdrivers.set(d.id, d);
            }
            this._sightingDialog.setVehicleDriverList(drivers);
            filter.setDriverList(drivers);
        }
        if (behaviours) {
            for (const b of behaviours) {
                mbehaviours.set(b.id, b);
            }
        }
        if (encates) {
            for (const c of encates) {
                mencates.set(c.id, c);
            }
            this._sightingDialog.setReactionList(encates);
        }
        if (organizations) {
            for (const o of organizations) {
                morganizations.set(o.id, o);
            }
            filter.setOrganizationList(organizations);
        }
        // List card -----------------------------------------------------------------------------
        const row1 = new ContentRow2(content.getContentFluidElement());
        const card = new Card2({});
        card.appendTo(new ContentCol2(row1, ContentColSize2.col12).getElement());
        card.setTitle(new LangTextV2({ text: 'Sighting' }));
        const btnMenu = new ButtonMenu2({ icon: IconFa.bars, positionRight: true });
        btnMenu.appendTo(card.getToolsElement());
        btnMenu.addMenuItem(new LangTextV2({ text: 'Filter' }), () => {
            filter.show();
        }, 'fa fa-filter');
        /*
         * Admin-only: trigger a full rebuild of derived movement data
         * (sighting_movement + sighting_movement_track). Synchronous on the
         * server side, so the button stays in a "running" state until the
         * server returns counts. Filter for admin happens after the
         * currentuser lookup further down — see below.
         */
        const rebuildMovementsLabel = new LangTextV2({ text: 'Rebuild movements' });
        const triggerRebuildMovements = async () => {
            var _a, _b, _c, _d;
            const confirmed = window.confirm((_a = Lang.i().l('Rebuild every sighting\'s movement track now? This may take a while.')) !== null && _a !== void 0 ? _a : 'Rebuild every sighting\'s movement track now? This may take a while.');
            if (!confirmed) {
                return;
            }
            this._toast.fire({
                icon: 'info',
                title: (_b = Lang.i().l('Rebuilding movements…')) !== null && _b !== void 0 ? _b : 'Rebuilding movements…'
            });
            try {
                const msg = await SightingMovementAPI.rebuildAll();
                this._toast.fire({
                    icon: 'success',
                    title: `${(_c = Lang.i().l('Rebuild done')) !== null && _c !== void 0 ? _c : 'Rebuild done'} (${msg})`
                });
                // Refresh the page so the new tracks show up on the map.
                if (this._onLoadTable) {
                    await this._onLoadTable();
                }
            }
            catch (err) {
                this._toast.fire({
                    icon: 'error',
                    title: `${(_d = Lang.i().l('Rebuild failed')) !== null && _d !== void 0 ? _d : 'Rebuild failed'}: ${err.message}`
                });
            }
        };
        /*
         * Admin-only: edit the persisted MovementConfig (lead/trail/outlier/tz).
         * Fetched fresh on each open so a hand-edit to the settings row from
         * another path (admin SQL, another browser tab) doesn't get clobbered.
         */
        const movementSettingsLabel = new LangTextV2({ text: 'Movement settings' });
        const openMovementSettings = async () => {
            var _a;
            try {
                const cfg = await SightingMovementAPI.getConfig();
                this._movementSettingsDialog.setTitle(new LangTextV2({ text: 'Movement settings' }));
                this._movementSettingsDialog.setConfig(cfg);
                this._movementSettingsDialog.show();
            }
            catch (err) {
                this._toast.fire({
                    icon: 'error',
                    title: `${(_a = Lang.i().l('Load failed')) !== null && _a !== void 0 ? _a : 'Load failed'}: ${err.message}`
                });
            }
        };
        this._movementSettingsDialog.setOnSave(async () => {
            var _a, _b;
            try {
                await SightingMovementAPI.saveConfig(this._movementSettingsDialog.getConfig());
                this._toast.fire({
                    icon: 'success',
                    title: (_a = Lang.i().l('Settings saved')) !== null && _a !== void 0 ? _a : 'Settings saved'
                });
                this._movementSettingsDialog.hide();
            }
            catch (err) {
                this._toast.fire({
                    icon: 'error',
                    title: `${(_b = Lang.i().l('Save failed')) !== null && _b !== void 0 ? _b : 'Save failed'}: ${err.message}`
                });
            }
        });
        /*
         * In-header quick-search — sibling of .card-title in the card-header so AdminLTE's
         * float layout puts it directly right of the title (and left of the tools menu).
         */
        const searchSlot = jQuery('<div class="sighting-quick-search"/>').insertAfter(card.getTitleElement());
        const searchInput = new Input2({ type: InputType2.text });
        searchInput.appendTo(searchSlot);
        searchInput.setPlaceholder(Lang.i().l('search note / recognizable …'));
        /*
         * Map tab on top of the table — the existing layout has both views side-by-side via NavTab,
         * we keep the same UX. Build the navigation manually since we want full control over which
         * body the table goes into.
         */
        const navContainer = jQuery('<ul class="nav nav-tabs"/>').appendTo(card.getElement());
        const tabBodies = jQuery('<div class="tab-content"/>').appendTo(card.getElement());
        const listTabId = `sighting-tab-list-${Date.now()}`;
        const mapTabId = `sighting-tab-map-${Date.now()}`;
        const analyticsTabId = `sighting-tab-analytics-${Date.now()}`;
        const yearCompareTabId = `sighting-tab-year-${Date.now()}`;
        const exportTabId = `sighting-tab-export-${Date.now()}`;
        const lang = Lang.i();
        navContainer.append(`<li class="nav-item"><a class="nav-link active" data-bs-toggle="tab" href="#${listTabId}">${lang.l('List')}</a></li>`);
        navContainer.append(`<li class="nav-item"><a class="nav-link" href="#${mapTabId}" data-bs-toggle="tab">${lang.l('Map')}</a></li>`);
        navContainer.append(`<li class="nav-item"><a class="nav-link" href="#${analyticsTabId}" data-bs-toggle="tab">${lang.l('Analytics')}</a></li>`);
        navContainer.append(`<li class="nav-item"><a class="nav-link" href="#${yearCompareTabId}" data-bs-toggle="tab">${lang.l('Year comparison')}</a></li>`);
        navContainer.append(`<li class="nav-item"><a class="nav-link" href="#${exportTabId}" data-bs-toggle="tab">${lang.l('Export')}</a></li>`);
        const listBody = jQuery(`<div class="tab-pane fade active show" id="${listTabId}"/>`).appendTo(tabBodies);
        const mapBody = jQuery(`<div class="tab-pane fade" id="${mapTabId}"/>`).appendTo(tabBodies);
        const analyticsBody = jQuery(`<div class="tab-pane fade" id="${analyticsTabId}"/>`).appendTo(tabBodies);
        const yearCompareBody = jQuery(`<div class="tab-pane fade" id="${yearCompareTabId}"/>`).appendTo(tabBodies);
        const exportBody = jQuery(`<div class="tab-pane fade" id="${exportTabId}"/>`).appendTo(tabBodies);
        // Dashboard (crossfilter + dc.js) sits on top of the map, fed from `loadedEntries` -------
        const dashboard = new SightingDashboard(mapBody);
        const mapContainer = jQuery('<div/>').appendTo(mapBody);
        /*
         * Redraw the map against whatever survived the dashboard's filters. The full
         * `loadedEntries` stay in the crossfilter — only what's shown on the map shrinks.
         */
        const redrawMap = async (filtered) => {
            if (!this._map) {
                return;
            }
            this._map.clearFeatureList();
            for (const entry of filtered) {
                this._addSightingToMap(entry, sightingLookups);
            }
            /*
             * Refresh the movements layer with the filtered set — but
             * only if the user has opened it. Otherwise we keep the
             * fetch deferred.
             */
            // eslint-disable-next-line no-use-before-define
            await refreshMovementsForEntries(filtered);
            await this._map.refrech();
        };
        /**
         * Chunked fetch + append. Keeps the browser responsive when the
         * filtered set is large — without this, a 5000-sighting filter
         * crashes the UI thread during GeoJSON parse + OL feature
         * insertion. 100 ids per request is enough to keep DB round-trips
         * low without producing megabytes per response.
         */
        const MOVEMENT_CHUNK_SIZE = 100;
        /**
         * A monotonically increasing sequence the chunked-load flow uses
         * as a race-guard: if the user toggles the layer or changes the
         * dashboard filter mid-load, every in-flight chunk that doesn't
         * match the current sequence number gets dropped on arrival.
         */
        let movementFetchSeq = 0;
        const sleepZero = () => new Promise((resolve) => {
            /*
             * Yield to the event loop between chunks so the page stays
             * interactive (scroll, layer toggle, etc.). setTimeout(0) is
             * good enough — we don't need a frame-aligned yield.
             */
            setTimeout(resolve, 0);
        });
        /**
         * Wipe the movements layer and re-populate it for `entries`
         * incrementally. The page calls this on layer toggle-on, on
         * dashboard filter change, and after a "load all" backfill. Each
         * chunk's features get appended as soon as they arrive so the
         * user sees tracks growing on the map instead of waiting for the
         * whole set.
         */
        const refreshMovementsForEntries = async (entries) => {
            if (!this._map || !this._map.isMovementsLayerVisible()) {
                return;
            }
            const seq = ++movementFetchSeq;
            this._map.clearMovementTracks();
            const ids = entries.map((e) => e.id);
            for (let i = 0; i < ids.length; i += MOVEMENT_CHUNK_SIZE) {
                const chunk = ids.slice(i, i + MOVEMENT_CHUNK_SIZE);
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const movements = await SightingMovementAPI.getList(chunk);
                    if (seq !== movementFetchSeq) {
                        /*
                         * A newer refresh started while this chunk was in
                         * flight — drop the result, the newer flow will
                         * repopulate everything from scratch.
                         */
                        return;
                    }
                    if (this._map) {
                        this._map.setMovementTracks(movements, true);
                    }
                }
                catch (err) {
                    console.warn('[Sighting] movement fetch failed', err);
                }
                // eslint-disable-next-line no-await-in-loop
                await sleepZero();
            }
        };
        /**
         * Append movements for a single new page of sightings (no clear).
         * Cheaper than `refreshMovementsForEntries` during infinite
         * scroll: we know the prior chunks are still valid, just need
         * tracks for the rows the user just brought in.
         */
        const appendMovementsForEntries = async (entries) => {
            if (!this._map || !this._map.isMovementsLayerVisible()) {
                return;
            }
            if (entries.length === 0) {
                return;
            }
            const seq = movementFetchSeq;
            try {
                const movements = await SightingMovementAPI.getList(entries.map((e) => e.id));
                if (seq !== movementFetchSeq) {
                    return;
                }
                if (this._map) {
                    this._map.setMovementTracks(movements, true);
                }
            }
            catch (err) {
                console.warn('[Sighting] movement append failed', err);
            }
        };
        dashboard.setLookups({ species: mspecies, vehicles: mvehicles });
        dashboard.setOnFiltered((filtered) => {
            redrawMap(filtered).catch(() => undefined);
        });
        // Analytics tab (d3 charts on the same loadedEntries) -----------------------------------
        const analytics = new SightingAnalytics(analyticsBody);
        analytics.setLookups({ species: mspecies });
        // Year-comparison tab (loads its own dataset, ignoring period filter) -------------------
        const yearCompare = new SightingYearCompare(yearCompareBody);
        let yearCompareLoaded = false;
        /*
         * Both Map and Analytics tabs work off `loadedEntries`. The list streams it page-by-page,
         * so on first tab-open we usually need to backfill the rest under the active filter.
         * Shared helper so Map and Analytics behave identically.
         */
        const refreshDataDashboards = () => {
            dashboard.setData(loadedEntries);
            analytics.setData(loadedEntries);
            /*
             * The dashboard just grew to its data-filled height, which changes
             * where the map below it starts — re-fit the map to the viewport.
             * Deferred a frame so the dashboard layout has settled before we
             * measure (otherwise the map would be sized against the empty,
             * short dashboard and overshoot the viewport bottom).
             */
            if (this._map) {
                window.requestAnimationFrame(() => {
                    if (!this._map) {
                        return;
                    }
                    // eslint-disable-next-line no-use-before-define
                    this._map.setHeight(computeMapHeight());
                    this._map.updateSize();
                });
            }
        };
        const triggerBackfillIfNeeded = () => {
            if (loadedCount >= totalCount) {
                refreshDataDashboards();
                return;
            }
            DialogConfirm2.confirm('loadallsightings', ModalDialogType2.xlarge, Lang.i().l('Load all ...'), `${Lang.i().l('Load all')} ${totalCount} ${Lang.i().l('sightings under the current filter?')}`, async (_, modal) => {
                const remaining = totalCount - loadedCount;
                const more = await SightingsAPI.getList(buildApiFilter(loadedCount, remaining));
                if (more) {
                    for (const entry of more.list) {
                        loadedEntries.push(entry);
                        this._addSightingToMap(entry, sightingLookups);
                    }
                    loadedCount += more.list.length;
                    /*
                     * Backfill brought in N new sightings — append
                     * tracks for those only (the existing chunks are
                     * still valid). Skips silently when the layer is
                     * hidden, so closed-layer "load all" stays cheap.
                     */
                    await appendMovementsForEntries(more.list);
                    if (this._map) {
                        await this._map.refrech();
                    }
                }
                refreshDataDashboards();
                modal.hide();
            });
        };
        // Map ----------------------------------------------------------------------------------
        const currentuser = await UserAPI.getUserInfo();
        let viewCenter = null;
        if (currentuser === null || currentuser === void 0 ? void 0 : currentuser.organization) {
            viewCenter = fromLonLat([
                parseFloat(currentuser.organization.lon),
                parseFloat(currentuser.organization.lat)
            ]);
        }
        /*
         * Admin-only menu entry. We add it here (after the user-info lookup)
         * rather than at card-build time so non-admins don't see a no-op item.
         */
        if ((_a = currentuser === null || currentuser === void 0 ? void 0 : currentuser.user) === null || _a === void 0 ? void 0 : _a.isAdmin) {
            btnMenu.addMenuItem(rebuildMovementsLabel, () => {
                triggerRebuildMovements().catch(() => undefined);
            }, 'fa fa-route');
            btnMenu.addMenuItem(movementSettingsLabel, () => {
                openMovementSettings().catch(() => undefined);
            }, 'fa fa-cog');
        }
        this._map = new SightingMap(mapContainer);
        /*
         * Fill the map down to the viewport bottom. Measure the map container's
         * own top offset (below the filter, tabs and the dashboard that sits
         * above it) and subtract it from the viewport height — this adapts to
         * whatever chrome the shell has (the AdminLTE 4 flip hides the page-title
         * header, so the old fixed `window.height() - 220 - 320` offset left a
         * large empty gap under the map). While the Map tab is still hidden the
         * container has no layout (top ≈ 0), so fall back to a window estimate
         * for the first (invisible) render; `shown.bs.tab` recomputes it.
         */
        const dashboardHeight = 320;
        const computeMapHeight = () => {
            var _a, _b;
            const top = (_b = (_a = mapContainer[0]) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) !== null && _b !== void 0 ? _b : 0;
            if (top <= 0) {
                return Math.max(300, window.innerHeight - 220 - dashboardHeight);
            }
            return Math.max(300, window.innerHeight - top - 16);
        };
        this._map.setHeight(computeMapHeight());
        this._map.load({
            useHeatmap: true,
            useBathymetriemap: true
        });
        this._map.setView(viewCenter);
        await this._map.addAreaByJson('map_areas/ES7020123.json', 'ES7020123', 'sigthing_ES7020123_layer');
        await this._map.addAreaByJson('map_areas/ES7020122.json', 'ES7020122', 'sigthing_ES7020122_layer');
        await this._map.addAreaByJson('map_areas/ES0000526.json', 'ES0000526', 'sigthing_ES0000526_layer');
        /*
         * Lazy-fetch movements only when the user opens the layer in
         * the LayerSwitcher. Visibility changes also fire when the layer
         * gets unchecked — we drop the source content in that case so
         * turning it back on always re-fetches the current visible set.
         */
        this._map.setOnMovementsVisibilityChange((visible) => {
            if (!this._map) {
                return;
            }
            if (!visible) {
                this._map.clearMovementTracks();
                return;
            }
            refreshMovementsForEntries(loadedEntries).catch(() => undefined);
        });
        navContainer.find(`a[href="#${mapTabId}"]`).on('shown.bs.tab', () => {
            if (!this._map) {
                return;
            }
            /*
             * Defer to the next frame: `shown.bs.tab` fires before the now-
             * visible dashboard above the map has settled its layout, so
             * measuring the map container's top offset immediately yields a
             * too-small value (map ends up too tall, overshooting the viewport).
             */
            window.requestAnimationFrame(() => {
                if (!this._map) {
                    return;
                }
                this._map.setHeight(computeMapHeight());
                this._map.updateSize();
            });
            triggerBackfillIfNeeded();
        });
        navContainer.find(`a[href="#${analyticsTabId}"]`).on('shown.bs.tab', () => {
            /*
             * The analytics charts are first drawn at load while this tab is
             * hidden (width 0 → fallback width). Re-render now that the tab is
             * visible so the D3 charts size to the real container width. Deferred
             * a frame so the pane layout has settled.
             */
            window.requestAnimationFrame(() => {
                analytics.setData(loadedEntries);
            });
            triggerBackfillIfNeeded();
        });
        /*
         * Year-on-year comparison ignores period_from/period_to (the whole point is to span
         * multiple years), but keeps every other filter so org/species/vehicle scoping still
         * applies. Loaded once per page-render and cached — re-run by reloading the page.
         */
        const loadYearCompareIfNeeded = async () => {
            if (yearCompareLoaded) {
                return;
            }
            yearCompareLoaded = true;
            const f = buildApiFilter(0, 100000);
            delete f.period_from;
            delete f.period_to;
            yearCompare.setData([]);
            const response = await SightingsAPI.getList(f);
            if (response) {
                yearCompare.setData(response.list);
            }
        };
        navContainer.find(`a[href="#${yearCompareTabId}"]`).on('shown.bs.tab', () => {
            loadYearCompareIfNeeded().catch(() => undefined);
        });
        /*
         * Backfill all sightings under the active filter without prompting (used by the PDF
         * orchestrator — analytics + dashboard need the full set, but the user already
         * committed to "Generate PDF" so we don't ask twice).
         */
        const ensureAllSightingsLoaded = async () => {
            if (loadedCount >= totalCount) {
                refreshDataDashboards();
                return;
            }
            const remaining = totalCount - loadedCount;
            const more = await SightingsAPI.getList(buildApiFilter(loadedCount, remaining));
            if (more) {
                for (const entry of more.list) {
                    loadedEntries.push(entry);
                    this._addSightingToMap(entry, sightingLookups);
                }
                loadedCount += more.list.length;
                if (this._map) {
                    await this._map.refrech();
                }
            }
            refreshDataDashboards();
        };
        /*
         * Export tab — three sub-tabs (Excel Export / Excel Report / Data Page).
         * The Data Page button hands off to the page-side `generatePdf` orchestrator
         * since the widget can't trigger map/analytics rendering itself.
         */
        const generatePdf = async () => {
            await ensureAllSightingsLoaded();
            await loadYearCompareIfNeeded();
            const olMap = this._map;
            const $body = jQuery('body');
            /*
             * Make the panes visible on screen so D3 measures the right container width
             * and OL has a real canvas to draw into. The mwpa-print-mode CSS in
             * mwpa.css promotes any `.print-include` pane to display:block right away
             * (not just under @media print) — without this, the SVGs would already
             * have been drawn into a 0-width hidden container.
             */
            mapBody.addClass('print-include');
            analyticsBody.addClass('print-include');
            yearCompareBody.addClass('print-include');
            $body.addClass('mwpa-print-mode');
            /*
             * The panes are now visible. Let the layout settle one frame so jQuery
             * .width() reads the new sizes, then redraw the d3 charts so they fit
             * the actual on-screen width (not the 0 / 320-fallback they had while
             * hidden).
             */
            await new Promise((resolve) => {
                window.requestAnimationFrame(() => resolve(undefined));
            });
            analytics.setData(loadedEntries);
            /*
             * yearCompare already has its own data; re-set to force re-render at
             * the now-visible container width.
             * (loadYearCompareIfNeeded above only kicks off the network fetch.)
             * We don't store the year dataset on the page, so trigger via the
             * widget's own data: setData([]) then re-fetch would race; instead,
             * re-show via a fresh fetch only if the data wasn't there yet.
             * The simpler, deterministic move: force a re-render by passing the
             * same list back in. yearCompare keeps a reference internally on
             * first setData; without an accessor we re-fetch here.
             */
            const yearFilter = buildApiFilter(0, 100000);
            delete yearFilter.period_from;
            delete yearFilter.period_to;
            const yearResp = await SightingsAPI.getList(yearFilter);
            if (yearResp) {
                yearCompare.setData(yearResp.list);
            }
            /*
             * OL renders into the visible map container. After updating size it
             * requests new tiles asynchronously — wait for `rendercomplete` (with
             * a timeout fallback) so the print snapshot captures filled tiles.
             */
            if (olMap) {
                olMap.updateSize();
                await olMap.refrech();
                await new Promise((resolve) => {
                    /*
                     * Cannot listen on the SightingMap wrapper directly without an
                     * accessor — refrech() schedules a re-render and tiles load via
                     * the OL source. The hard timeout is a sane upper bound.
                     */
                    window.setTimeout(resolve, 1500);
                });
            }
            const cleanup = () => {
                $body.removeClass('mwpa-print-mode');
                mapBody.removeClass('print-include');
                analyticsBody.removeClass('print-include');
                yearCompareBody.removeClass('print-include');
                window.removeEventListener('afterprint', cleanup);
                /*
                 * Re-set analytics width with normal layout so on-screen SVGs match
                 * the (now smaller) Bootstrap-tab container.
                 */
                analytics.setData(loadedEntries);
                /*
                 * Map's container is back to its dashboard-tab dimensions — let OL
                 * resize so the on-screen view stays correct.
                 */
                if (olMap) {
                    olMap.updateSize();
                }
            };
            window.addEventListener('afterprint', cleanup);
            try {
                window.print();
            }
            finally {
                // Belt-and-braces: some browsers don't fire afterprint reliably.
                window.setTimeout(cleanup, 1500);
            }
        };
        // eslint-disable-next-line no-new
        new SightingExport(exportBody, () => ({
            period_from: filterValues.period_from,
            period_to: filterValues.period_to,
            species_id: filterValues.species_id,
            organization_id: filterValues.organization_id,
            vehicle_id: filterValues.vehicle_id,
            vehicle_driver_id: filterValues.vehicle_driver_id,
            search: inHeaderSearch === '' ? filterValues.search : inHeaderSearch
        }), generatePdf);
        // Table ---------------------------------------------------------------------------------
        const tableWrapper = new TableWrapper2({ tableOptions: { head_fixed: true } });
        tableWrapper.appendTo(listBody);
        const table = tableWrapper.getTable();
        const trhead = new Tr2();
        trhead.appendTo(table.getThead());
        /*
         * Sort registry: each call wires a header to its key in `sort` and ensures clicking it
         * resets every other header (single-column sort).
         */
        const sortRegistry = [];
        const makeSortColumn = (label, key) => {
            const column = new LangTextV2({
                text: label,
                onClick: (langText) => {
                    const newValue = SortingColumn2.changeSort(langText, sort[key]);
                    for (const other of sortRegistry) {
                        if (other.key !== key) {
                            sort[other.key] = SortOrder2.NONE;
                            SortingColumn2.initSort(other.column, SortOrder2.NONE);
                        }
                    }
                    sort[key] = newValue;
                    tableWrapper.reset();
                }
            });
            SortingColumn2.initSort(column, sort[key]);
            sortRegistry.push({ key, column });
            return column;
        };
        const thIdTourDate = new Th2({});
        thIdTourDate.addComponent(makeSortColumn('Id', 'id'));
        thIdTourDate.append('<br>');
        thIdTourDate.addComponent(makeSortColumn('TourId', 'tour_id'));
        thIdTourDate.append('<br>');
        thIdTourDate.addComponent(makeSortColumn('Date', 'date'));
        thIdTourDate.appendTo(trhead);
        const thVehicle = new Th2({});
        thVehicle.addComponent(new LangTextV2({ text: 'Vehicle' }));
        thVehicle.append('<br>');
        thVehicle.addComponent(new LangTextV2({ text: 'Driver' }));
        thVehicle.append('<br>');
        thVehicle.addComponent(new LangTextV2({ text: 'Organization' }));
        thVehicle.appendTo(trhead);
        const thSpecies = new Th2({});
        thSpecies.addComponent(new LangTextV2({ text: 'Species' }));
        thSpecies.append('<br>');
        thSpecies.addComponent(new LangTextV2({ text: 'Other species' }));
        thSpecies.appendTo(trhead);
        const thGroupSize = new Th2({});
        thGroupSize.addComponent(new LangTextV2({ text: 'Group-Size' }));
        thGroupSize.append('<br>');
        thGroupSize.addComponent(new LangTextV2({ text: 'Subgroups' }));
        thGroupSize.appendTo(trhead);
        const thTime = new Th2({});
        thTime.addComponent(makeSortColumn('Time begin-end', 'tour_start'));
        thTime.append('<br>');
        thTime.addComponent(new LangTextV2({ text: 'Duration' }));
        thTime.appendTo(trhead);
        new Th2({ value: new LangTextV2({ text: 'Location' }) }).appendTo(trhead);
        new Th2({ value: new LangTextV2({ text: 'Distance<br>(Miles)' }) }).appendTo(trhead);
        const thPhotos = new Th2({});
        thPhotos.addComponent(new LangTextV2({ text: 'Photos taken' }));
        thPhotos.append('<br>');
        thPhotos.addComponent(new LangTextV2({ text: 'E. without GPS' }));
        thPhotos.appendTo(trhead);
        const thBehaviour = new Th2({});
        thBehaviour.addComponent(new LangTextV2({ text: 'Behaviour' }));
        thBehaviour.append('<br>');
        thBehaviour.addComponent(new LangTextV2({ text: 'Reaction' }));
        thBehaviour.appendTo(trhead);
        const thCreated = new Th2({});
        thCreated.addComponent(makeSortColumn('Created', 'create_datetime'));
        thCreated.append('<br>');
        thCreated.addComponent(new LangTextV2({ text: 'Created by' }));
        thCreated.append('<br>');
        thCreated.addComponent(makeSortColumn('Updated', 'update_datetime'));
        thCreated.appendTo(trhead);
        new Th2({ value: '' }).appendTo(trhead);
        // Renderer ------------------------------------------------------------------------------
        const renderRow = (_table, entry) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            const term = activeHighlight();
            const trbody = new Tr2();
            trbody.appendTo(table.getTbody());
            /* Historical hand-maintained rows (sighting_old): muted style + "ALT" badge, read-only. */
            if (entry.is_old) {
                trbody.getElement().addClass('mwpa-old-row').css('background-color', '#f5f1e8');
            }
            const date = moment((_a = entry.date) === null || _a === void 0 ? void 0 : _a.split(' ')[0]);
            const oldBadge = entry.is_old
                ? ' <span class="badge badge-secondary" title="Alte Daten – manuell erfasst, schreibgeschützt"><i class="fas fa-archive"></i> ALT</span>'
                : '';
            new Td2({
                value: `<b>#${entry.id}</b>${oldBadge}<br>#${entry.tour_id}<br>${date.format('YYYY.MM.DD')}`
            }).appendTo(trbody);
            const vehicleName = (_c = (_b = mvehicles.get(entry.vehicle_id)) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '';
            const driverName = (_e = (_d = mdrivers.get(entry.vehicle_driver_id)) === null || _d === void 0 ? void 0 : _d.user.name) !== null && _e !== void 0 ? _e : '';
            const orgStr = entry.organization_id ? (_g = (_f = morganizations.get(entry.organization_id)) === null || _f === void 0 ? void 0 : _f.description) !== null && _g !== void 0 ? _g : '' : '';
            new Td2({
                value: `${escapeHtml(vehicleName)}<br>${escapeHtml(driverName)}<br>${escapeHtml(orgStr)}`
            }).appendTo(trbody);
            const otherSpecies = SightingPopup.parseIdMapNames((_h = entry.other_species) !== null && _h !== void 0 ? _h : '', (id) => { var _a; return (_a = mspecies.get(id)) === null || _a === void 0 ? void 0 : _a.name.split(',')[0]; }).join(', ');
            const speciesTd = new Td2({ value: '' });
            speciesTd.appendTo(trbody);
            // eslint-disable-next-line no-new
            new SpeciesDisplay(speciesTd.getElement(), entry, mspecies, (speciesId) => {
                Nav.load(new SpeciesProfilePage(speciesId)).then();
            });
            speciesTd.append(`<br>${escapeHtml(otherSpecies)}`);
            const speciesCountGroupTd = new Td2({ value: '' });
            speciesCountGroupTd.appendTo(trbody);
            if (entry.species_count === 0) {
                const badgeZero = new Badge2({ type: BadgeType2.info, color: 'red' });
                badgeZero.appendTo(speciesCountGroupTd);
                badgeZero.getElement().html('<b style="color: white">0</b>');
            }
            else {
                speciesCountGroupTd.append(`<b>${entry.species_count}</b>`);
            }
            speciesCountGroupTd.append(`<br>${UtilSelect.getSelectStr(entry.subgroups)}`);
            new Td2({
                value: `<b>${escapeHtml((_j = entry.tour_start) !== null && _j !== void 0 ? _j : '')} - ${escapeHtml((_k = entry.tour_end) !== null && _k !== void 0 ? _k : '')}</b><br>${escapeHtml((_l = entry.duration_from) !== null && _l !== void 0 ? _l : '')} - ${escapeHtml((_m = entry.duration_until) !== null && _m !== void 0 ? _m : '')}`
            }).appendTo(trbody);
            const tdLocation = new Td2({ value: '' });
            tdLocation.appendTo(trbody);
            // eslint-disable-next-line no-new
            new LocationDisplay(tdLocation.getElement(), entry.location_begin, () => {
                Nav.load(new ToursMap(entry.tour_id)).then();
            });
            /*
             * Track-point indicator. Always rendered so the user can spot
             * at a glance which sightings have a real boat GPS track
             * behind them (segment_count + 1, grey) vs which only have
             * hand-entered begin/end anchors or no movement at all (0, red).
             */
            const trackCount = (_o = entry.track_point_count) !== null && _o !== void 0 ? _o : 0;
            const trackLabel = (_p = Lang.i().l('track pts')) !== null && _p !== void 0 ? _p : 'track pts';
            if (trackCount > 0) {
                tdLocation.append(`<small class="text-muted"><i class="fas fa-route mr-1"></i>${trackCount} ${trackLabel}</small>`);
            }
            else {
                tdLocation.append(`<small style="color:#dc3545"><i class="fas fa-route mr-1"></i>0 ${trackLabel}</small>`);
            }
            const fDistance = parseFloat(entry.distance_coast) || 0;
            new Td2({ value: `${UtilDistanceCoast.meterToM(fDistance, true)}` }).appendTo(trbody);
            new Td2({
                value: `<b>${UtilSelect.getSelectStr(entry.photo_taken)}</b><br>${UtilSelect.getSelectStr(entry.distance_coast_estimation_gps)}`
            }).appendTo(trbody);
            const behaviourNames = SightingPopup.parseIdMapNames((_q = entry.behaviours) !== null && _q !== void 0 ? _q : '', (id) => { var _a; return (_a = mbehaviours.get(id)) === null || _a === void 0 ? void 0 : _a.name; });
            const behaviourStr = behaviourNames.length > 0
                ? behaviourNames.map((n) => `${n}<br>`).join('')
                : 'not set<br>';
            const tdbehRex = new Td2({ value: behaviourStr });
            tdbehRex.appendTo(trbody);
            // eslint-disable-next-line no-new
            new ReactionDisplay(tdbehRex.getElement(), entry, mencates);
            const createDate = moment(entry.create_datetime * 1000);
            const updateDate = moment(entry.update_datetime * 1000);
            new Td2({
                value: `<b>${createDate.format('YYYY.MM.DD HH:mm:ss')}</b><br>${highlight(entry.creater_name, term)}<br>${updateDate.format('YYYY.MM.DD HH:mm:ss')}`
            }).appendTo(trbody);
            /*
             * Note column gets the search highlight too — small affordance so the user can
             * see *why* a row matched a free-text search.
             */
            if (term !== '' && entry.note) {
                trbody.getElement().attr('title', entry.note);
            }
            const tdAction = new Td2({ value: '' });
            tdAction.appendTo(trbody);
            // Old (hand-imported) rows are read-only — no edit/delete menu.
            if (entry.is_old) {
                tdAction.append('<small class="text-muted"><i class="fas fa-lock"></i></small>');
                return;
            }
            const abtnMenu = new ButtonMenu2({ icon: IconFa.bars, positionRight: true });
            abtnMenu.appendTo(tdAction);
            abtnMenu.addMenuItem(Lang.i().l('Edit'), () => {
                const distanceCoast = parseFloat(entry.distance_coast) || 0;
                this._sightingDialog.setTitle(Lang.i().l('Edit Sighting'));
                this._sightingDialog.resetValues();
                this._sightingDialog.setSightingId(entry.id);
                this._sightingDialog.setTourId(entry.tour_id);
                this._sightingDialog.setVehicle(entry.vehicle_id);
                this._sightingDialog.setVehicleDriver(entry.vehicle_driver_id);
                this._sightingDialog.setBeaufortWind(entry.beaufort_wind);
                this._sightingDialog.setDateSight(entry.date);
                this._sightingDialog.setTourStart(entry.tour_start);
                this._sightingDialog.setTourEnd(entry.tour_end);
                this._sightingDialog.setDurationFrom(entry.duration_from);
                this._sightingDialog.setDurationUntil(entry.duration_until);
                this._sightingDialog.setPositionBegin(entry.location_begin);
                this._sightingDialog.setPositionEnd(entry.location_end);
                this._sightingDialog.setDistanceCoast(UtilDistanceCoast.meterToM(distanceCoast, true));
                this._sightingDialog.setSpecie(entry.species_id);
                this._sightingDialog.setSpeciesCount(entry.species_count);
                this._sightingDialog.setReaction(entry.reaction_id);
                this._sightingDialog.setOther(entry.other);
                this._sightingDialog.setOtherBoats(entry.other_vehicle);
                this._sightingDialog.setNote(entry.note);
                this._sightingDialog.show();
            }, IconFa.edit);
            abtnMenu.addDivider();
            /*
             * Per-vessel GFW breakdown — opens a small modal listing
             * each fishing vessel that was active in the 25 km buffer
             * on the sighting day. Empty list shows a "no fishing
             * vessels for this sighting" placeholder so the user can
             * tell "no activity" from "endpoint failed".
             */
            abtnMenu.addMenuItem(Lang.i().l('Fishing vessels'), async () => {
                var _a, _b;
                let vessels = [];
                try {
                    vessels = await SightingFishingVesselAPI.getList([entry.id]);
                }
                catch (e) {
                    this._toast.fire({ icon: 'error', title: e.message });
                    return;
                }
                const body = jQuery('<div/>');
                if (vessels.length === 0) {
                    body.html('<p class="text-muted">No fishing vessels recorded for this sighting (no activity, or GFW pull hasn\'t run yet).</p>');
                }
                else {
                    const lines = ['<table class="table table-sm table-striped">'];
                    lines.push('<thead><tr><th>Vessel</th><th>Flag</th><th>Gear</th><th class="text-right">Hours</th></tr></thead><tbody>');
                    for (const v of vessels) {
                        const name = v.name && v.name !== '' ? v.name : `<code class="small">${escapeHtml(v.vessel_id)}</code>`;
                        const mmsi = v.mmsi && v.mmsi !== '' ? `<br><small class="text-muted">MMSI ${escapeHtml(v.mmsi)}</small>` : '';
                        lines.push('<tr>'
                            + `<td>${name}${mmsi}</td>`
                            + `<td>${escapeHtml((_a = v.flag) !== null && _a !== void 0 ? _a : '—')}</td>`
                            + `<td>${escapeHtml((_b = v.gear_type) !== null && _b !== void 0 ? _b : '—')}</td>`
                            + `<td class="text-right">${v.hours.toFixed(2)}</td>`
                            + '</tr>');
                    }
                    lines.push('</tbody></table>');
                    body.html(lines.join(''));
                }
                DialogInfo2.info('fishingvessels', ModalDialogType2.large, `Fishing vessels — Sighting #${entry.id}`, body, (_, modal) => modal.hide());
            }, 'fa fa-ship');
            abtnMenu.addDivider();
            abtnMenu.addMenuItem(Lang.i().l('Delete'), () => {
                this._sightingDeletedDialog.setTitle(Lang.i().l('Delete a sighting'));
                this._sightingDeletedDialog.resetValues();
                this._sightingDeletedDialog.setSightingId(entry.id);
                this._sightingDeletedDialog.show();
            }, IconFa.trash);
            if (entry.files.length > 0) {
                tdAction.append('<br>');
                const btnAttachment = new ButtonMenu2({ icon: IconFa.paperclip, positionRight: true });
                btnAttachment.appendTo(tdAction);
                for (const fname of entry.files) {
                    btnAttachment.addMenuItem(fname, () => {
                        const aImage = new Image();
                        aImage.src = `/json/sightings/getimage/${entry.id}/${fname}`;
                        aImage.style.width = '100%';
                        DialogInfo2.info('sightimage', ModalDialogType2.xlarge, `Image for Sighting #${entry.id}`, 
                        // @ts-ignore
                        JQuery(aImage), (_, modal) => modal.hide());
                    }, IconFa.camera);
                }
            }
            this._addSightingToMap(entry, sightingLookups);
        };
        // Hook the data source — TableWrapper drives infinite scroll itself ----------------------
        this._onLoadTable = async () => {
            await tableWrapper.reset();
        };
        tableWrapper.setDataSource(async (page) => {
            card.showLoading();
            try {
                const offset = page * PAGE_SIZE;
                const response = await SightingsAPI.getList(buildApiFilter(offset, PAGE_SIZE));
                if (!response) {
                    return [];
                }
                if (page === 0) {
                    // Reset only on first page so subsequent infinite-scroll pages add to existing markers.
                    if (this._map) {
                        this._map.clearFeatureList();
                    }
                    loadedCount = 0;
                    loadedEntries.length = 0;
                    dashboard.clear();
                    analytics.clear();
                    yearCompare.clear();
                    yearCompareLoaded = false;
                }
                for (const entry of response.list) {
                    loadedEntries.push(entry);
                }
                /*
                 * If the user already has the movements layer open,
                 * append tracks for the newly arrived page only —
                 * cheaper than re-fetching for the whole accumulated
                 * set on every infinite-scroll tick.
                 */
                await appendMovementsForEntries(response.list);
                totalCount = response.count;
                loadedCount += response.list.length;
                card.setTitle(`${Lang.i().l('Sighting')} (${response.count})`);
                return response.list;
            }
            finally {
                card.hideLoading();
                Lang.i().lAll();
                Tooltip2.init();
                if (this._map) {
                    await this._map.refrech();
                }
            }
        }, renderRow);
        // Wire the in-header quick-search (debounced) -------------------------------------------
        let searchTimer = null;
        searchInput.getElement().on('keyup', () => {
            if (searchTimer !== null) {
                clearTimeout(searchTimer);
            }
            searchTimer = setTimeout(() => {
                const value = searchInput.getValue().trim();
                if (value === inHeaderSearch) {
                    return;
                }
                inHeaderSearch = value;
                tableWrapper.reset();
            }, 300);
        });
        // Wire filter Apply / Reset -------------------------------------------------------------
        filter.setOnApply((values) => {
            filterValues.period_from = values.period_from;
            filterValues.period_to = values.period_to;
            filterValues.species_id = values.species_id;
            filterValues.organization_id = values.organization_id;
            filterValues.vehicle_id = values.vehicle_id;
            filterValues.vehicle_driver_id = values.vehicle_driver_id;
            filterValues.search = values.search;
            filterValues.include_old = values.include_old;
            tableWrapper.reset();
        });
    }
    /**
     * Push one sighting onto the map (used both by the table renderer and the bulk
     * "load all" backfill). Silently ignores entries without a valid begin location.
     */
    _addSightingToMap(entry, lookups) {
        var _a;
        if (!this._map) {
            return;
        }
        const bgeol = UtilLocation.strToGeolocationCoordinates(entry.location_begin);
        if (!bgeol) {
            return;
        }
        const objectType = (_a = entry.pointtype) !== null && _a !== void 0 ? _a : `${SightingMapObjectType.Testudines}`;
        this._map.addSighting(objectType, entry.unid, () => this._buildSightingPopup(entry, lookups), UtilLocation.geoLocationToOlCoordinates(bgeol));
    }
    /**
     * Build the rich map-popover content for a sighting: the coloured species
     * badge plus the key fields (mirroring the list columns). Returned as a
     * detached div; the map serialises it to an HTML string for the Bootstrap
     * popover, so the content is static (no live click handlers).
     * @param {SightingsEntry} entry
     * @param {SightingPopupLookups} lookups
     * @return {JQuery}
     * @protected
     */
    _buildSightingPopup(entry, lookups) {
        var _a, _b, _c;
        const lang = Lang.i();
        const div = jQuery('<div class="sighting-popup" style="min-width:230px;"/>');
        // Species silhouette (own centered row, from images/species/*.svg) + badge.
        const specie = entry.species_id ? lookups.species.get(entry.species_id) : undefined;
        const silName = (_b = (_a = specie === null || specie === void 0 ? void 0 : specie.name) !== null && _a !== void 0 ? _a : entry.other) !== null && _b !== void 0 ? _b : '';
        const silWrap = jQuery('<div style="display:flex;justify-content:center;margin-bottom:4px;"/>').appendTo(div);
        (_c = silWrap[0]) === null || _c === void 0 ? void 0 : _c.insertAdjacentHTML('beforeend', UtilSpeciesSilhouette.img(silName, 54));
        const head = jQuery('<div class="mb-1" style="text-align:center;"/>').appendTo(div);
        // eslint-disable-next-line no-new
        new SpeciesDisplay(head, entry, lookups.species);
        const rows = SightingPopup.rows(entry, lookups);
        const trs = rows
            .filter(([, value]) => value !== '')
            .map(([label, value]) => '<tr><td style="color:#6c757d;white-space:nowrap;vertical-align:top;padding:1px 8px 1px 0;">' +
            `${escapeHtml(lang.l(label))}</td>` +
            `<td style="vertical-align:top;padding:1px 0;">${escapeHtml(value)}</td></tr>`)
            .join('');
        jQuery(`<table style="font-size:0.82rem;border-collapse:collapse;"><tbody>${trs}</tbody></table>`).appendTo(div);
        return div;
    }
}
